import { z } from "zod";

import { setUiPublicName } from "@loom/shared/brand";

import { httpGet, UnauthorizedError } from "../lib/http";

export const AuthUserSchema = z.object({
  userId: z.string(),
  gitlabHost: z.string(),
  username: z.string(),
  name: z.string().optional(),
  email: z.string().optional(),
  // Access-control gate (server-owned). The server always returns these now;
  // they're modelled as required to match the live contract. The schema is not
  // `.strict()`, so an OLDER server that omitted them would fail the required
  // parse — acceptable since the gating server ships them together.
  approvalStatus: z.enum(["pending", "approved", "blocked"]),
  isAdmin: z.boolean(),
  // The UI public-name mode (dev only, `LOOM_UI_PUBLIC_NAME`). Present only
  // when on; an older server leaves it out, which reads as off.
  publicName: z.boolean().optional(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

/**
 * How long a signed-in answer is reused. One page load asks from several
 * places — the route guard, then the admin link or the account page it
 * mounts — and those now share one request. Short enough that a later in-app
 * navigation asks the server again, as it always has.
 */
export const ME_TTL_MS = 10_000;

// Module state: one load's shared answer. `epoch` stops a request that was
// already running when the cache was dropped from filling it again.
let epoch = 0;
let inflight: Promise<AuthUser | null> | null = null;
let cached: { readonly user: AuthUser; readonly at: number } | null = null;

/**
 * Forget the shared answer. Called where who-is-signed-in may have changed: a
 * Loom sign-in that ended (`auth/unauthorized.ts`), a sign-out, the pending
 * page's Re-check, the reauth popup's sign-in (`auth/ReauthBanner.tsx`), and a
 * page restored from the back/forward cache. An account switch in this tab is
 * a full reload through `/auth/gitlab/start`, which starts this module fresh.
 */
export const invalidateMe = (): void => {
  epoch += 1;
  inflight = null;
  cached = null;
};

/**
 * The page is back in front of the user. While it was not, they may have
 * signed out, or in as someone else, in another tab or window, and nothing
 * tells this page that. So the kept answer goes. A request already running is
 * still shared (it is this load's: a guard waiting on it must not start a
 * second one), but its answer is not kept, so the first caller after it asks
 * the server again. Without this a guard or a freshly mounted page could show
 * the previous user for up to `ME_TTL_MS`.
 */
const forgetKeptAnswer = (): void => {
  epoch += 1;
  cached = null;
};

/**
 * Who is signed in, or `null` when no one is. Calls made while one request
 * runs share it, and a signed-in answer is reused for `ME_TTL_MS`. Nothing
 * else is kept: a `null` (401), a 429, a 5xx or a network error is never
 * cached, so the error screen's Retry really asks again. The server stays the
 * authority — every other API checks the session itself.
 */
export const fetchMe = (): Promise<AuthUser | null> => {
  if (cached !== null && Date.now() - cached.at < ME_TTL_MS) {
    return Promise.resolve(cached.user);
  }
  if (inflight !== null) return inflight;
  // The answer is kept only while `epoch` is unchanged: `invalidateMe` and
  // `forgetKeptAnswer` move it.
  const mine = epoch;
  const request = (async (): Promise<AuthUser | null> => {
    try {
      const user = await httpGet("/auth/me", AuthUserSchema);
      // The UI public-name mode: on from the first answer that says so, and
      // never turned back off during this page load. Every signed-in route
      // waits for this answer before it draws, so no line shows "Loom" first
      // (`src/i18n/publicName.ts`).
      if (user.publicName === true) setUiPublicName(true);
      if (mine === epoch) cached = { user, at: Date.now() };
      return user;
    } catch (err) {
      if (err instanceof UnauthorizedError) return null;
      throw err;
    }
  })();
  inflight = request;
  // Cleared by its own end only while it is still the shared one:
  // `invalidateMe` may have replaced it with a newer request meanwhile.
  const settle = (): void => {
    if (inflight === request) inflight = null;
  };
  void request.then(settle, settle);
  return request;
};

if (typeof window !== "undefined") {
  // A page restored from the back/forward cache keeps this module's state from
  // whenever it was frozen, possibly before a sign-out in another page.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) invalidateMe();
  });
  // Back in front of the user after being left: another tab or window, or a
  // popup, may have changed who is signed in. `visibilitychange` covers a tab
  // switch, `blur` / `focus` a window beside this one (both fire for the window
  // only: neither bubbles from elements). Only a return counts: a page that
  // takes focus as it loads (after the address bar, say) has not been left,
  // and forgetting then would cost the load a second `/auth/me`.
  let away = false;
  const back = (): void => {
    if (!away) return;
    away = false;
    forgetKeptAnswer();
  };
  window.addEventListener("blur", () => {
    away = true;
  });
  window.addEventListener("focus", back);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") away = true;
    else back();
  });
}

export const signInUrl = (returnTo?: string): string => {
  const params = new URLSearchParams();
  if (returnTo !== undefined) params.set("return_to", returnTo);
  const qs = params.toString();
  return `/auth/gitlab/start${qs ? `?${qs}` : ""}`;
};
